import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { FiSearch, FiX, FiChevronLeft, FiChevronRight, FiRefreshCw, FiCheck, FiUser, FiUsers, FiMove } from 'react-icons/fi';
import '../styles/UserSelectModal.css';
import { useAuth } from '../context/AuthContext';
import { useNotify } from '../context/NotificationContext';

const UserSelectModal = ({ 
  isOpen, 
  onClose, 
  onSelectUser, 
  selectedUserId = '', 
  allowAllUsers = true,
  allowedRoles = [], // Array of roles to filter by, e.g. ['Partner', 'Staff Mgr']
  allowedUserIds = [], // Array of specific user IDs to show
  title = "Select User",
  allUsersLabel = "All Users" 
}) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  // Drag and resize state
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [modalPosition, setModalPosition] = useState({ x: 0, y: 0 });
  const [modalSize, setModalSize] = useState({ width: 500, height: 600 });
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, width: 0, height: 0 });
  
  const { APP_URL, getCookie } = useAuth();
  const { error, spinner } = useNotify();
  const modalRef = useRef(null);
  const searchInputRef = useRef(null);
  const headerRef = useRef(null);

  // Memoize the filter strings to prevent infinite re-renders
  const rolesFilter = useMemo(() => {
    return allowedRoles && allowedRoles.length > 0 ? allowedRoles.join(',') : '';
  }, [allowedRoles]);

  const userIdsFilter = useMemo(() => {
    return allowedUserIds && allowedUserIds.length > 0 ? allowedUserIds.join(',') : '';
  }, [allowedUserIds]);

  // Reset modal position and size when opened
  useEffect(() => {
    if (isOpen) {
      // Center the modal
      const centerX = (window.innerWidth - 500) / 2;
      const centerY = (window.innerHeight - 600) / 2;
      setModalPosition({ x: Math.max(0, centerX), y: Math.max(0, centerY) });
      setModalSize({ width: 500, height: 600 });
    }
  }, [isOpen]);

  // Drag functionality
  const handleMouseDown = useCallback((e) => {
    if (headerRef.current && headerRef.current.contains(e.target)) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - modalPosition.x,
        y: e.clientY - modalPosition.y
      });
    }
  }, [modalPosition]);

  const handleMouseMove = useCallback((e) => {
    if (isDragging) {
      const newX = Math.max(0, Math.min(e.clientX - dragStart.x, window.innerWidth - modalSize.width));
      const newY = Math.max(0, Math.min(e.clientY - dragStart.y, window.innerHeight - modalSize.height));
      setModalPosition({ x: newX, y: newY });
    } else if (isResizing) {
      const deltaX = e.clientX - resizeStart.x;
      const deltaY = e.clientY - resizeStart.y;
      const newWidth = Math.max(400, Math.min(resizeStart.width + deltaX, window.innerWidth - modalPosition.x));
      const newHeight = Math.max(300, Math.min(resizeStart.height + deltaY, window.innerHeight - modalPosition.y));
      setModalSize({ width: newWidth, height: newHeight });
    }
  }, [isDragging, isResizing, dragStart, resizeStart, modalPosition, modalSize]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setIsResizing(false);
  }, []);

  // Resize functionality
  const handleResizeMouseDown = useCallback((e) => {
    e.stopPropagation();
    setIsResizing(true);
    setResizeStart({
      x: e.clientX,
      y: e.clientY,
      width: modalSize.width,
      height: modalSize.height
    });
  }, [modalSize]);

  // Add global event listeners for drag and resize
  useEffect(() => {
    if (isDragging || isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.userSelect = 'none';
      document.body.style.cursor = isDragging ? 'move' : 'se-resize';
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.userSelect = '';
        document.body.style.cursor = '';
      };
    }
  }, [isDragging, isResizing, handleMouseMove, handleMouseUp]);

  const fetchUsers = () => {
    setLoading(true);
    const { complete } = spinner("Loading users...");
    
    // Build query parameters
    const params = new URLSearchParams();
    params.append('page', currentPage.toString());
    params.append('per_page', itemsPerPage.toString());
    
    // Add search if present
    if (searchQuery.trim()) {
      params.append('search_term', searchQuery.trim());
    }
    
    // Add role filter if present
    // if (rolesFilter) {
    //   params.append('roles', rolesFilter);
    // }
    
    // Add specific user IDs filter if present
    if (userIdsFilter) {
      params.append('user_ids', userIdsFilter);
    }
    
    console.log('=== DEBUG INFO ===');
    console.log('allowedRoles prop:', allowedRoles);
    console.log('rolesFilter:', rolesFilter);
    console.log('Fetching users with params:', params.toString());
    console.log('Full URL:', `${APP_URL}/users?${params.toString()}`);
    
    fetch(`${APP_URL}/users?${params.toString()}`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'X-CSRF-TOKEN': getCookie('csrf_access_token'),
        'Content-Type': 'application/json',
      }
    })
      .then(resp => {
        if (resp.ok) {
          return resp.json();
        } else {
          error('Failed to fetch users');
          return null;
        }
      })
      .then(data => {
        if (data) {
          console.log('=== API RESPONSE ===');
          console.log('Received users data:', data);
          console.log('Number of users:', data.items ? data.items.length : data.length);
          console.log('User roles:', data.items ? data.items.map(u => u.role) : data.map(u => u.role));
          
          // Handle paginated response
          if (data.items) {
            setUsers(data.items);
            setTotalPages(data.pagination.total_pages || 1);
            setTotalItems(data.pagination.total_items || 0);
          } else {
            // Handle legacy format for backward compatibility
            setUsers(data);
            setTotalPages(1);
            setTotalItems(data.length);
          }
          complete(true, '', true);
        } else {
          complete(false);
        }
      })
      .catch(e => {
        error(e);
        complete(false);
      })
      .finally(() => setLoading(false));
  };

  // Load users when the modal opens or page changes or filters change
  useEffect(() => {
    if (isOpen) {
      fetchUsers();
      // Focus the search input when modal opens
      if (currentPage === 1) { // Only focus on first page load
        setTimeout(() => {
          if (searchInputRef.current) {
            searchInputRef.current.focus();
          }
        }, 100);
      }
    }
  }, [isOpen, currentPage, itemsPerPage, rolesFilter, userIdsFilter]); // Use memoized strings

  // Reset page when search query changes
  useEffect(() => {
    if (isOpen && searchQuery !== '') {
      setCurrentPage(1);
    }
  }, [searchQuery, isOpen]);

  // Handle click outside to close modal (but not when dragging/resizing)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!isDragging && !isResizing && modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose, isDragging, isResizing]);

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && !isDragging && !isResizing) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isOpen, onClose, isDragging, isResizing]);

  const handleSearch = () => {
    setCurrentPage(1); // Reset to first page when searching
    fetchUsers();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handlePreviousPage = () => {
    setCurrentPage(prev => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setCurrentPage(prev => Math.min(prev + 1, totalPages));
  };

  const handlePageSizeChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1); // Reset to first page when changing page size
  };

  const handleSelectUser = (user) => {
    onSelectUser(user);
    onClose();
  };

  const handleSelectAllUsers = () => {
    onSelectUser({ id: '', username: allUsersLabel, role: '' });
    onClose();
  };

  // Sort users by role and then alphabetically by username
  const sortUsers = (users) => {
    // Define role priorities
    const rolePriority = {
      'Partner': 1,
      'Staff Mgr': 2,
      'Staff': 3
    };

    return [...users].sort((a, b) => {
      // Compare roles
      const roleA = rolePriority[a.role] || 999; // Default high value for unknown roles
      const roleB = rolePriority[b.role] || 999;

      if (roleA !== roleB) {
        return roleA - roleB; // Sort by role priority
      }

      // If roles are the same, sort alphabetically by username
      return a.username.localeCompare(b.username);
    });
  };

  // Get role badge class
  const getRoleBadgeClass = (role) => {
    switch (role) {
      case 'Partner':
        return 'user-select-role role-partner';
      case 'Staff Mgr':
        return 'user-select-role role-manager';
      case 'Staff':
        return 'user-select-role role-staff';
      default:
        return 'user-select-role';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="user-select-modal-overlay">
      <div 
        className="user-select-modal draggable-modal" 
        ref={modalRef}
        style={{
          left: `${modalPosition.x}px`,
          top: `${modalPosition.y}px`,
          width: `${modalSize.width}px`,
          height: `${modalSize.height}px`,
          position: 'fixed'
        }}
      >
        <div 
          className="user-select-modal-header draggable-header"
          ref={headerRef}
          onMouseDown={handleMouseDown}
        >
          <div className="header-content">
            <FiMove className="drag-icon" />
            <h2>{title}</h2>
          </div>
          <button className="user-select-modal-close" onClick={onClose}>
            <FiX />
          </button>
        </div>
        
        <div className="user-select-modal-search">
          <div className="user-select-search-input-group">
            <input
              type="text"
              className="user-select-search-input"
              placeholder="Search by name, email, or role"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              ref={searchInputRef}
            />
            <button className="user-select-search-button" onClick={handleSearch}>
              <FiSearch />
            </button>
            {searchQuery && (
              <button 
                className="user-select-clear-search-button" 
                onClick={() => {
                  setSearchQuery('');
                  setCurrentPage(1);
                  setTimeout(fetchUsers, 0);
                }}
              >
                <FiX />
              </button>
            )}
          </div>
          
          <div className="user-select-items-per-page">
            <label>Show:</label>
            <select 
              value={itemsPerPage} 
              onChange={handlePageSizeChange}
              disabled={loading}
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={20}>20</option>
            </select>
          </div>
        </div>
        
        <div className="user-select-modal-content">
          {/* Option for All Users */}
          {allowAllUsers && (
            <div 
              className={`user-select-item ${selectedUserId === '' ? 'selected' : ''}`}
              onClick={handleSelectAllUsers}
            >
              <div className="user-select-item-info">
                <div className="user-select-item-avatar all-users">
                  <FiUsers />
                </div>
                <div className="user-select-item-details">
                  <div className="user-select-item-name">{allUsersLabel}</div>
                  <div className="user-select-item-id">View data for all users</div>
                </div>
              </div>
              {selectedUserId === '' && (
                <div className="user-select-item-check">
                  <FiCheck />
                </div>
              )}
            </div>
          )}
          
          {/* User List */}
          {sortUsers(users).map(user => (
            <div 
              key={user.id}
              className={`user-select-item ${selectedUserId === user.id.toString() ? 'selected' : ''}`}
              onClick={() => handleSelectUser(user)}
            >
              <div className="user-select-item-info">
                <div className="user-select-item-avatar">
                  {user.username.charAt(0).toUpperCase()}
                </div>
                <div className="user-select-item-details">
                  <div className="user-select-item-name">
                    {user.username}
                    <span className={getRoleBadgeClass(user.role)}>{user.role}</span>
                  </div>
                  <div className="user-select-item-id">
                    {user.email || `ID: ${user.id}`}
                  </div>
                </div>
              </div>
              {selectedUserId === user.id.toString() && (
                <div className="user-select-item-check">
                  <FiCheck />
                </div>
              )}
            </div>
          ))}
          
          {loading && (
            <div className="user-select-loading">
              <FiRefreshCw className="spinner" />
              <p>Loading users...</p>
            </div>
          )}
          
          {!loading && users.length === 0 && (
            <div className="user-select-no-results">
              <p>No users found. Try adjusting your search.</p>
            </div>
          )}
        </div>
        
        {totalItems > 0 && (
          <div className="user-select-pagination">
            <div className="user-select-pagination-info">
              Showing {Math.min((currentPage - 1) * itemsPerPage + 1, totalItems)} to {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} users
            </div>
            <div className="user-select-pagination-controls">
              <button 
                className="user-select-pagination-button"
                onClick={handlePreviousPage}
                disabled={currentPage === 1 || loading}
              >
                <FiChevronLeft />
              </button>
              <span className="user-select-pagination-current">
                {currentPage} of {totalPages}
              </span>
              <button 
                className="user-select-pagination-button"
                onClick={handleNextPage}
                disabled={currentPage === totalPages || loading}
              >
                <FiChevronRight />
              </button>
            </div>
          </div>
        )}
        
        {/* Resize handle */}
        <div 
          className="resize-handle"
          onMouseDown={handleResizeMouseDown}
        />
      </div>
    </div>
  );
};

export default UserSelectModal;
